
import licenses from '../licenses.json';

import { defineComponent } from 'vue';
import PalaLink from '@/ui/components/bases/PalaLink.vue';
import PaladiumIcon from '@/ui/components/bases/icons/PaladiumIcon.vue';
import AvatarIcon from '@/ui/components/bases/icons/AvatarIcon.vue';
import PlayCircleIcon from '@/ui/components/bases/icons/PlayCircleIcon.vue';
import RAMIcon from '@/ui/components/bases/icons/RAMIcon.vue';
import ScreenIcon from '@/ui/components/bases/icons/ScreenIcon.vue';
import LegalIcon from '@/ui/components/bases/icons/LegalIcon.vue';
import SearchIcon from '@/ui/components/bases/icons/SearchIcon.vue';
import XIcon from '@/ui/components/bases/icons/XIcon.vue';
import CogIcon from '@/ui/components/bases/icons/CogIcon.vue';
import UserCard from '@/ui/components/settings/UserCard.vue';
import Toggle from '@/ui/components/Toggle.vue';
import * as Games from '../../../domain/Games';
import {requestAction, requestGetter, requestSetter} from "../../../utils/cef";
import {Game} from "../../../typings/games";
import { ui, setDiagnosticResult } from '../../../domain/ui';

type MapTypeBool = {
  [id: string]: boolean;
}

type MapTypeAny = {
  [id: string]: any;
}

interface SearchEntry {
  label: string;
  icon: string;
  page: string;
  target: string;
  kw: string;
}

export default defineComponent({
  name: 'SettingsModal',
  components: {
    PalaLink,
    PaladiumIcon,
    AvatarIcon,
    PlayCircleIcon,
    RAMIcon,
    ScreenIcon,
    LegalIcon,
    SearchIcon,
    XIcon,
    CogIcon,
    UserCard,
    Toggle,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    defaultTab: {
      type: [String, Number],
      default: 0,
    },
  },
  emits: ['close'],
  data() {
    return {
      activeGames:([] as Game[]),
      licenses,
      totalMem: 0,
      freeMem: 0,
      gamesMem: ({} as MapTypeAny),
      gamesResolution: ({} as MapTypeAny),
      gamesStartInFullscreen: ({} as MapTypeBool),
      gamesLauncherStayOpen: ({} as MapTypeBool),
      uuid: '',
      accounts: ([] as { username: string, skinUrl: string, uuid: string }[]),
      accountConnecting: false,
      connectTimer: (0 as any),
      loginBridge: (null as any),
      current: 'games',
      query: '',
      highlightKey: (null as any),
      hlTimer: (0 as any),
      pulseOn: false,
      pulseTimer: (0 as any),
      diagCopied: false,
      resolutionPresets: [
        { label: '854×480', w: 854, h: 480 },
        { label: '1280×720', w: 1280, h: 720 },
        { label: '1600×900', w: 1600, h: 900 },
        { label: '1920×1080', w: 1920, h: 1080 },
      ],
      searchIndex: ([
        { label: 'Ajouter un compte', icon: 'AvatarIcon', page: 'accounts', target: 'add', kw: 'ajouter compte comptes connexion microsoft add account accounts new login sign in' },
        { label: 'Changer de compte actif', icon: 'AvatarIcon', page: 'accounts', target: 'list', kw: 'compte comptes actif utiliser changer switch change account accounts active use user profile pseudo' },
        { label: 'Se déconnecter d\'un compte', icon: 'AvatarIcon', page: 'accounts', target: 'list', kw: 'compte comptes deconnexion deconnecter supprimer logout log out sign out disconnect remove account accounts' },
        { label: 'Mémoire RAM', icon: 'RAMIcon', page: 'games', target: 'ram', kw: 'ram memoire allouee go jeu paladium memory allocated gb java heap xmx performance' },
        { label: 'Résolution', icon: 'ScreenIcon', page: 'games', target: 'res', kw: 'resolution largeur hauteur taille fenetre ecran presets jeu width height window size screen display 1920 1080 1280 720' },
        { label: 'Démarrer Minecraft en plein écran', icon: 'ScreenIcon', page: 'games', target: 'fs', kw: 'plein ecran demarrer minecraft jeu fullscreen full screen start borderless maximized' },
        { label: 'Garder le launcher ouvert après le lancement', icon: 'PlayCircleIcon', page: 'games', target: 'after', kw: 'garder launcher ouvert apres lancement fermer keep launcher open stay open after launch close exit game' },
        { label: 'Lancer le diagnostic', icon: 'CogIcon', page: 'diag', target: 'diag', kw: 'diagnostic rapport logs support probleme bug diagnostics report troubleshoot debug issue' },
        { label: 'Conditions générales de vente', icon: 'LegalIcon', page: 'legal', target: 'terms', kw: 'cgv conditions vente mentions legales legal terms of sale' },
        { label: 'Conditions générales d\'utilisation', icon: 'LegalIcon', page: 'legal', target: 'terms', kw: 'cgu conditions utilisation mentions legales legal terms of use tos service' },
        { label: 'Licences tierces', icon: 'LegalIcon', page: 'legal', target: 'lic', kw: 'licences tierces composants open source mentions legales legal licenses license third party libraries credits' },
      ] as SearchEntry[]),
    };
  },
  computed: {
    navGroups(): { label: string, pages: { id: string, label: string, icon: string }[] }[] {
      return [
        { label: 'Général', pages: [
          { id: 'accounts', label: 'Comptes', icon: 'AvatarIcon' },
          { id: 'games', label: 'Jeux', icon: 'PlayCircleIcon' },
        ] },
        { label: 'Aide', pages: [
          { id: 'diag', label: 'Diagnostic', icon: 'CogIcon' },
          { id: 'legal', label: 'Mentions légales', icon: 'LegalIcon' },
        ] },
      ];
    },
    game(): Game | null {
      return this.activeGames.find((g) => g.id === 'paladium') || this.activeGames[0] || null;
    },
    gameName(): string {
      return this.game ? this.game.name : 'Paladium';
    },
    searchResults(): SearchEntry[] {
      const words = this.norm(this.query).split(/\s+/).filter(Boolean);
      if (!words.length) {
        return [];
      }
      return this.searchIndex.filter((e) => {
        const hay = this.norm(e.label + ' ' + e.kw);
        return words.every((w) => hay.indexOf(w) !== -1);
      });
    },
    ramMax(): number {
      return Math.min(this.totalMem > 0 ? this.totalMem : 16, 16);
    },
    ramTickList(): { v: number, major: boolean }[] {
      const max = this.ramMax;
      const out: { v: number, major: boolean }[] = [];
      for (let v = 2; v <= max + 1e-9; v += 1) {
        out.push({ v, major: v % 2 === 0 });
      }
      const last = out[out.length - 1];
      if (last && last.v < max - 1e-9) {
        if (max - last.v < 1.5 && last.major) {
          last.major = false;
        }
        out.push({ v: max, major: true });
      }
      return out;
    },
    diagStatus(): string {
      return ui.diagnostic.status;
    },
    diagSteps(): any[] {
      return ui.diagnostic.steps || [];
    },
    endStatus(): string {
      if (this.diagStatus === 'done') {
        return 'ok';
      }
      if (this.diagStatus === 'error') {
        return 'skipped';
      }
      return 'pending';
    },
    endStatusLabel(): string {
      if (this.diagStatus === 'done') {
        return 'Rapport généré';
      }
      if (this.diagStatus === 'error') {
        return 'Non atteint';
      }
      return 'Rapport de diagnostic';
    },
    runButtonLabel(): string {
      if (this.diagStatus === 'running') {
        return 'Diagnostic en cours…';
      }
      if (this.diagStatus === 'done' || this.diagStatus === 'error') {
        return 'Relancer le diagnostic';
      }
      return 'Lancer le diagnostic';
    },
    diagError(): { step: string, sub: string | null } | null {
      const step = this.diagSteps.find((s: any) => s.status === 'ko');
      if (!step) {
        return null;
      }
      const sub = (step.subs || []).find((x: any) => x.status === 'ko');
      return { step: step.name, sub: sub ? sub.l : null };
    },
  },
  watch: {
    open(value: boolean) {
      if (value) {
        const dt = String(this.defaultTab || '');
        const pages = ['accounts', 'games', 'diag', 'legal'];
        this.current = pages.indexOf(dt) !== -1 ? dt : (dt.length > 0 ? 'games' : 'accounts');
        this.query = '';
        if (this.current === 'diag') {
          this.runDiagnostic();
        }
      }
    },
  },
  async beforeMount() {
    requestGetter("MEMORY", undefined, (data: any) => {
        if (data.status === 200) {
            this.totalMem = data.payload.totalMem;
            this.freeMem = data.payload.freeMem;
        }
    });

    this.loadAccounts();

      requestGetter("IS_LOGGED", {},(logdata: any) => {
          if (logdata.status === 200) {
              requestGetter("GET_TOKEN", {}, (ttdata: any) => {
                  if (ttdata.status !== 200 || !ttdata.payload || !ttdata.payload.jwt) {
                      return;
                  }
                  Games.getGames(ttdata.payload.jwt).then((games: Game[]) => {
                      games.forEach((game: Game) => {
                          if (game.visibility && game.id === 'paladium') {
                              this.activeGames.push(game);

                              requestGetter("GAME_SETTINGS", {id: game.settingsId}, (data: any) => {
                                  if (data.status === 200) {
                                      this.gamesMem[game.settingsId] = data.payload.memory;
                                      this.gamesResolution[game.settingsId] = data.payload.resolution;
                                      this.gamesStartInFullscreen[game.settingsId] = data.payload.startInFullscreen;
                                      this.gamesLauncherStayOpen[game.settingsId] = data.payload.launcherStayOpen;
                                  }
                              });
                          }
                      })
                  }).catch(() => undefined);
              });
          }
      });
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown);
    this.pulseTimer = setInterval(() => { this.pulseOn = !this.pulseOn; }, 650);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown);
    if (this.hlTimer) {
      clearTimeout(this.hlTimer);
    }
    if (this.pulseTimer) {
      clearInterval(this.pulseTimer);
    }
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
    }
    if ((window as any).loginFinished === this.loginBridge) {
      (window as any).loginFinished = undefined;
    }
  },
  methods: {
    onKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape' && this.open) {
        this.close();
      }
    },
    close() {
      this.$emit('close');
    },
    norm(value: string): string {
      return (value || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    },
    go(id: string) {
      this.current = id;
      this.query = '';
      this.$nextTick(() => {
        const main = this.$refs.main as HTMLElement | undefined;
        if (main) {
          main.scrollTop = 0;
        }
      });
    },
    openResult(entry: SearchEntry) {
      this.go(entry.page);
      this.$nextTick(() => {
        const main = this.$refs.main as HTMLElement | undefined;
        if (!main) {
          return;
        }
        const target = main.querySelector('[data-hl="' + entry.target + '"]');
        if (target) {
          this.highlightKey = entry.target;
          target.scrollIntoView({ block: 'nearest' });
          if (this.hlTimer) {
            clearTimeout(this.hlTimer);
          }
          this.hlTimer = setTimeout(() => { this.highlightKey = null; }, 1600);
        }
      });
    },
    onSearchKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        this.query = '';
      } else if (event.key === 'Enter') {
        const first = this.searchResults[0];
        if (first) {
          this.openResult(first);
        }
      }
    },
    clampInt(value: any, min: number, max: number, fallback: number): number {
      const parsed = parseInt(value, 10);
      if (isNaN(parsed)) {
        return fallback;
      }
      return Math.min(Math.max(parsed, min), max);
    },
    updateSettings(settingsId: string) {
      const memory = this.gamesMem[settingsId];
      const resolution = this.gamesResolution[settingsId];
      if (!memory || !resolution) {
        return;
      }

      resolution.w = this.clampInt(resolution.w, 640, 15360, 854);
      resolution.h = this.clampInt(resolution.h, 480, 8640, 480);

      requestSetter("GAME_SETTINGS", {
        id: settingsId,
        memory: memory,
        resolution: resolution,
        startInFullscreen: this.gamesStartInFullscreen[settingsId],
        launcherStayOpen: this.gamesLauncherStayOpen[settingsId],
      }, undefined);
    },
    setGameStartInFullscreen(settingsId: string, value: boolean) {
      this.gamesStartInFullscreen[settingsId] = value;
      this.updateSettings(settingsId);
    },
    setLauncherStayOpen(settingsId: string, value: boolean) {
      this.gamesLauncherStayOpen[settingsId] = value;
      this.updateSettings(settingsId);
    },
    loadAccounts() {
      requestGetter('ACCOUNTS', undefined, (data: any) => {
        if (data.status === 200) {
          const list: { username: string, skinUrl: string, uuid: string }[] = [];
          for (const i in data.payload) {
            const ac = data.payload[i];
            list.push({
              username: ac.name,
              skinUrl: `https://crafthead.net/helm/${ac.uuid}?scale=8`,
              uuid: ac.uuid,
            });
          }
          this.accounts = list;
        }
      });
      requestGetter("UUID", undefined, (data: any) => {
        if (data.status === 200) {
          this.uuid = data.payload.uuid;
        }
      });
    },
    addAccount() {
        if (this.accountConnecting) {
          return;
        }
        requestGetter("GET_LOGIN_LINK", undefined, (data: any) => {
            if (data.status === 200 && data.payload && data.payload.link) {
                this.accountConnecting = true;
                this.loginBridge = () => this.onLoginFinished();
                (window as any).loginFinished = this.loginBridge;
                if (this.connectTimer) {
                  clearTimeout(this.connectTimer);
                }
                this.connectTimer = setTimeout(() => { this.accountConnecting = false; }, 180000);
                requestAction("POPUP",
                    {
                        title: "Connexion",
                        url: data.payload.link,
                        width: 536,
                        height: 693,
                    }, undefined);
            }
        });
    },
    onLoginFinished() {
      this.accountConnecting = false;
      if (this.connectTimer) {
        clearTimeout(this.connectTimer);
      }
      this.loadAccounts();
    },
    runDiagnostic() {
      ui.diagnostic.status = 'running';
      ui.diagnostic.path = '';
      ui.diagnostic.content = '';
      this.diagCopied = false;
      ui.diagnostic.steps.forEach((step: any) => {
        step.status = 'pending';
        step.progress = 0;
        (step.subs || []).forEach((sub: any) => {
          sub.status = 'pending';
          sub.v = '';
        });
      });
      requestGetter("DIAGNOSTIC", undefined, (data: any) => {
        if (data && data.status === 200 && data.payload && (data.payload.status || data.payload.path)) {
          setDiagnosticResult(data.payload);
        }
      });
    },
    stepStatusLabel(step: any): string {
      const count = (step.subs || []).length;
      switch (step.status) {
        case 'pending': return 'En attente';
        case 'skipped': return 'Non atteint';
        case 'active': {
          if (!count) {
            return 'En cours';
          }
          const done = step.subs.filter((x: any) => x.status === 'ok' || x.status === 'ko').length;
          return done + ' / ' + count;
        }
        case 'ok': return count ? count + ' vérifications' : 'Terminé';
        case 'ko': return 'Échec';
        default: return '';
      }
    },
    railHeight(step: any): string {
      if (step.status === 'pending' || step.status === 'skipped') {
        return '0%';
      }
      return Math.round((step.progress || 0) * 100) + '%';
    },
    lineClass(status: string): string {
      return status === 'ok' ? 'ok' : status === 'ko' ? 'ko' : '';
    },
    subValue(sub: any): string {
      switch (sub.status) {
        case 'ok':
        case 'ko': return sub.v || '';
        case 'active': return 'vérification';
        case 'skipped': return '—';
        default: return '';
      }
    },
    copyInfo() {
      if (this.diagCopied) {
        return;
      }
      const content = ui.diagnostic.content;
      if (content && navigator.clipboard) {
        navigator.clipboard.writeText(content).catch(() => undefined);
      }
      this.diagCopied = true;
      setTimeout(() => { this.diagCopied = false; }, 2200);
    },
    ramValue(settingsId: string): number {
      const m = this.gamesMem[settingsId];
      const v = m ? parseFloat(m.max) : 4;
      return isNaN(v) ? 4 : v;
    },
    setRam(settingsId: string, event: Event) {
      if (!this.gamesMem[settingsId]) {
        return;
      }
      const v = parseFloat((event.target as HTMLInputElement).value);
      this.gamesMem[settingsId].max = v;
      this.gamesMem[settingsId].min = v;
      this.updateSettings(settingsId);
    },
    ramLabel(value: number): string {
      return value % 1 === 0 ? String(value) : value.toFixed(1).replace('.', ',');
    },
    ramVars(settingsId: string) {
      const span = this.ramMax - 2;
      const value = this.ramValue(settingsId);
      const p = span <= 0 ? 0 : Math.max(0, Math.min(1, (value - 2) / span));
      const pa = span <= 0 ? 1 : Math.max(0, Math.min(1, (this.freeMem - 2) / span));
      return {
        '--p': p,
        '--pa': pa,
        '--pf': Math.min(p, pa),
        '--po': Math.max(0, p - pa),
      };
    },
    tickX(value: number): number {
      const span = this.ramMax - 2;
      return span <= 0 ? 0 : Math.max(0, Math.min(1, (value - 2) / span));
    },
    applyPreset(settingsId: string, preset: { w: number, h: number }) {
      if (!this.gamesResolution[settingsId]) {
        return;
      }
      this.gamesResolution[settingsId].w = preset.w;
      this.gamesResolution[settingsId].h = preset.h;
      this.updateSettings(settingsId);
    },
    isCurrentPreset(settingsId: string, preset: { w: number, h: number }): boolean {
      const r = this.gamesResolution[settingsId];
      return !!r && parseInt(r.w, 10) === preset.w && parseInt(r.h, 10) === preset.h;
    },
  },
});
